import { useState, useMemo, useCallback, useEffect } from "react";
import { UseServerSidePaginationOptions, UseServerSidePaginationReturn } from "../../../types/pagination.types";

export const DOTS = '...';

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const useServerSidePagination = ({
  totalItems,
  itemsPerPage,
  initialPage = 1,
  siblingCount = 1,
}: UseServerSidePaginationOptions): UseServerSidePaginationReturn => {
  const [currentPage, setCurrentPage] = useState(initialPage);

  const totalPages = useMemo(() => {
    if (itemsPerPage <= 0) return 0;
    return Math.ceil(totalItems / itemsPerPage);
  }, [totalItems, itemsPerPage]);

  const offset = useMemo(() => {
    if (itemsPerPage <= 0) return 0;
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage, itemsPerPage]);

  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + 5;

    if (totalPageNumbers >= totalPages) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, lastPageIndex];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPages - rightItemCount + 1, totalPages);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
    return [];
  }, [totalPages, currentPage, siblingCount]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    if (totalPages > 0) {
      setCurrentPage((prevPage) => Math.min(totalPages, prevPage + 1));
    }
  }, [totalPages]);

  const goToPage = useCallback((pageNumber: number) => {
    const page = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(page);
  }, [totalPages]);
  
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
        setCurrentPage(totalPages);
    } else if (totalPages === 0 && currentPage !== 1) {
        setCurrentPage(1);
    } else if (initialPage !== currentPage && totalPages > 0 && initialPage <= totalPages) {
    }
  }, [totalPages, currentPage, initialPage]);

  return {
    currentPage,
    setCurrentPage,
    handlePreviousPage,
    handleNextPage,
    goToPage,
    totalPages,
    offset,
    paginationRange,
  };
};